import type React from "react";
import { useState } from "react";

import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";

import type { Goal } from "api/models/Goal";
import { useSavedInputsContext } from "contexts/SavedInputsContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { Notification } from "shared/atoms/Notification";
import { deleteGoal } from "shared/molecules/PatientGoal/queries/setGoal";

const ErrorWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface Props {
  goal?: Partial<Goal>;
  patient: { id: number; most_painful_location?: { location: string } };
  patientId: string;
  setGoal: (goal: undefined | Partial<Goal>) => void;
  canDeleteGoal: boolean;
  notSet: string;
  success: boolean;
  isPhysicalFunctionAvailable: () => boolean;
  isFeelingAvailable: () => boolean;
  isPainAvailable: () => boolean;
  error?: Error | null;
}

export const NonEditGoal: React.FC<Props> = ({
  goal,
  patient,
  notSet,
  canDeleteGoal,
  patientId,
  setGoal,
  success,
  error,
  isPhysicalFunctionAvailable,
  isFeelingAvailable,
  isPainAvailable,
}) => {
  const { setSavedGoals } = useSavedInputsContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const [deleteError, setDeleteError] = useState<boolean>(false);

  const onDelete = async () => {
    if (goal?.id !== undefined) {
      const currentGoal = goal;
      try {
        setGoal(undefined);
        await deleteGoal(Number(patientId), goal.id);
        setDeleteError(false);
        setSavedGoals((goals: Record<string, Partial<Goal>>) => ({
          ...goals,
          [patient?.id]: undefined,
        }));
      } catch {
        setGoal(currentGoal);
        setDeleteError(true);
      }
    }
  };

  if (error || deleteError) {
    return (
      <ErrorWrapper>
        <Notification variant="danger">{t("errors.generic")}</Notification>
      </ErrorWrapper>
    );
  }

  return (
    <>
      <Row>
        <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
          {t("dashboard.tables.calls.goal.description")}
        </Typography>
        <Typography variant="subtitle1" data-testid="patient-goal-description">
          {goal?.free_text ?? notSet}
        </Typography>
      </Row>

      {isPainAvailable() && (
        <Row>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
            {t("dashboard.tables.calls.goal.pain")}
          </Typography>
          <Typography variant="subtitle1" data-testid="patient-goal-pain">
            {goal?.pain ?? notSet}
          </Typography>
        </Row>
      )}

      {isPhysicalFunctionAvailable() && (
        <Row>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
            {t("dashboard.tables.calls.goal.function")}
          </Typography>
          <Typography variant="subtitle1" data-testid="patient-goal-functionality">
            {goal?.functionality ?? notSet}
          </Typography>
        </Row>
      )}

      {isFeelingAvailable() && (
        <Row>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
            {t("dashboard.tables.calls.goal.feeling")}
          </Typography>
          <Typography variant="subtitle1" data-testid="patient-goal-pain">
            {goal?.feeling ?? notSet}
          </Typography>
        </Row>
      )}

      {success && <Notification variant="success">{t("dashboard.tables.calls.goal.success")}</Notification>}
      {canDeleteGoal && (
        <BaseButton
          data-testid="delete-goal-button"
          variant="danger"
          small
          style={{ alignSelf: "flex-start", marginTop: "16px" }}
          onClick={onDelete}
        >
          {t("buttons.delete_goal")}
        </BaseButton>
      )}
    </>
  );
};

export const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: 1px solid ${props => props.theme.colors.redesign.db10};
  padding: 16px 0;
  width: 100%;
`;
